// Guida.tsx — v045: la prima volta che si apre l'app (il proprietario: «deve essere estremamente facile, anche per un
// bambino o una persona di 80 anni»).
// - Benvenuto: una finestra con la mascotte che spunta sopra il bordo (v046: niente manine; la faccia è uno dei nostri
//   avatar, a caso, la stessa per tutta l'apertura dell'app):
//   «Ciao! Sei qui per la prima volta? Ti spiego come funziona» in tre righe, poi «Fammi vedere!» o «Lo so già».
// - GuidaLuci: lo schermo si scurisce e si illumina SOLO la cosa da toccare, con un fumetto della mascotte che dice a
//   cosa serve. Un passo alla volta (Avanti / Salta). Nell'ultimo passo si può toccare davvero la cosa illuminata.
// Visti una volta: con l'account valgono su tutti i telefoni (sono nei «visti» dei tutorial: t.benv, t.home, t.store).
// Con «riduci animazioni» niente rimbalzi né pulsazioni (lo fa il CSS).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { srcAvatar } from "./Avatar.tsx";
import { AVATAR_NUMERO } from "../../../condiviso/avatar.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { vistiCambiati } from "../servizi/account.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { vibra } from "../servizi/nativo.ts";
import { IMP_APP, PREFERENZE } from "../costanti.ts";

/** v046: la mascotte è uno dei nostri avatar, scelto a caso a ogni apertura dell'app (sempre lo stesso fino alla prossima). */
export const MASCOTTE = 1 + Math.floor(Math.random() * AVATAR_NUMERO);

export type ChiaveGuida = "benv" | "home" | "store";

function visti(): Record<string, boolean> {
  try {
    const v = JSON.parse(leggiPreferenza(PREFERENZE.tutorial) ?? "{}") as unknown;
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}
export function guidaVista(k: ChiaveGuida): boolean {
  return visti()[k] === true;
}
export function segnaGuida(k: ChiaveGuida): void {
  if (guidaVista(k)) return;
  salvaPreferenza(PREFERENZE.tutorial, JSON.stringify({ ...visti(), [k]: true }));
  vistiCambiati();
}
/** La guida è accesa dal pannello e non è ancora stata vista. */
export function serveGuida(k: ChiaveGuida): boolean {
  return leggiImpostazione(IMP_APP.guidaAttiva, true, eBooleano) && !guidaVista(k);
}

export function Mascotte({ dimensione = 96, classe = "" }: { dimensione?: number; classe?: string }) {
  return <img className={`ld-mascotte ${classe}`} src={srcAvatar(MASCOTTE, dimensione)} width={dimensione} height={dimensione} alt="" draggable={false} decoding="async" />;
}

/** Una finestra con la mascotte che spunta sopra il bordo. */
export function CartaMascotte({ children, etichetta, onEsc }: { children: ReactNode; etichetta: string; onEsc?: () => void }) {
  const rif = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // il primo pulsante prende il fuoco (tastiera, lettori di schermo)
    rif.current?.querySelector<HTMLElement>("button")?.focus();
    if (!onEsc) return;
    const tasto = (e: KeyboardEvent) => e.key === "Escape" && onEsc();
    window.addEventListener("keydown", tasto);
    return () => window.removeEventListener("keydown", tasto);
  }, [onEsc]);
  return createPortal(
    <div className="ld-mascotte-sfondo" role="dialog" aria-modal="true" aria-label={etichetta}>
      <div className="ld-mascotte-carta" ref={rif}>
        <div className="ld-mascotte-testa" aria-hidden="true">
          <Mascotte dimensione={132} />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** «Ciao! Sei qui per la prima volta?» */
export function Benvenuto({ onGuida, onChiudi }: { onGuida: () => void; onChiudi: () => void }) {
  const { t } = useTranslation();
  const chiudi = () => {
    segnaGuida("benv");
    onChiudi();
  };
  return (
    <CartaMascotte etichetta={t("guida.benvenuto_titolo")} onEsc={chiudi}>
      <h2 className="ld-mascotte-titolo">{t("guida.benvenuto_titolo")}</h2>
      <p className="ld-mascotte-testo">{t("guida.benvenuto_testo")}</p>
      <ol className="ld-benvenuto-passi">
        {(["passo1", "passo2", "passo3"] as const).map((k, i) => (
          <li key={k} style={{ animationDelay: `${250 + i * 220}ms` }}>
            <span className="ld-benvenuto-icona" aria-hidden="true">
              {["📲", "🤪", "😂"][i]}
            </span>
            <span>{t(`guida.${k}`)}</span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        className="ld-cta ld-cta-centrata"
        onClick={() => {
          vibra("leggero");
          segnaGuida("benv");
          onGuida();
        }}
      >
        {t("guida.fammi_vedere")}
      </button>
      <button type="button" className="pulsante-link ld-mascotte-salta" onClick={chiudi}>
        {t("guida.lo_so")}
      </button>
    </CartaMascotte>
  );
}

export interface PassoGuida {
  /** selettore CSS della cosa da illuminare (se non c'è nella pagina, il passo si salta) */
  sel: string;
  testo: string;
}

interface Riquadro {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

const MARGINE = 8;

/** Lo schermo scuro con un buco luminoso sulla cosa da toccare e il fumetto della mascotte. */
export function GuidaLuci({ passi, chiave, onFine }: { passi: PassoGuida[]; chiave: ChiaveGuida; onFine: () => void }) {
  const { t } = useTranslation();
  // solo i passi la cui cosa c'è davvero (un riquadro spento dal pannello non si spiega): si guarda dopo che la
  // pagina è stata disegnata (la guida può nascere insieme alle cose che spiega)
  const [validi, setValidi] = useState<PassoGuida[] | null>(null);
  useEffect(() => {
    const id = window.setTimeout(() => setValidi(passi.filter((p) => document.querySelector(p.sel))), 120);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [n, setN] = useState(0);
  const [box, setBox] = useState<Riquadro | null>(null);
  const passo = validi?.[n];
  const ultimo = !!validi && n === validi.length - 1;
  const fine = useCallback(() => {
    segnaGuida(chiave);
    onFine();
  }, [chiave, onFine]);

  useEffect(() => {
    if (validi && !validi.length) fine();
  }, [validi, fine]);

  const misura = useCallback(() => {
    const el = passo ? document.querySelector<HTMLElement>(passo.sel) : null;
    if (!el) return setBox(null);
    const r = el.getBoundingClientRect();
    const raggio = parseFloat(getComputedStyle(el).borderRadius) || 14;
    setBox({ x: r.left - MARGINE, y: r.top - MARGINE, w: r.width + MARGINE * 2, h: r.height + MARGINE * 2, r: Math.min(raggio + MARGINE, (r.height + MARGINE * 2) / 2) });
  }, [passo]);

  useLayoutEffect(() => {
    const el = passo ? document.querySelector<HTMLElement>(passo.sel) : null;
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "auto" });
    misura();
    // dopo lo scorrimento e le animazioni d'entrata della pagina
    const id = setTimeout(misura, 350);
    window.addEventListener("resize", misura);
    window.addEventListener("scroll", misura, true);
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", misura);
      window.removeEventListener("scroll", misura, true);
    };
  }, [passo, misura]);

  const bottoneRif = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    bottoneRif.current?.focus();
    const tasto = (e: KeyboardEvent) => e.key === "Escape" && fine();
    window.addEventListener("keydown", tasto);
    return () => window.removeEventListener("keydown", tasto);
  }, [n, fine]);

  if (!passo || !validi) return null;
  const avanti = () => {
    vibra("leggero");
    if (ultimo) fine();
    else setN((x) => x + 1);
  };
  // l'ultimo passo: toccare la cosa illuminata la usa davvero (e chiude la guida)
  const toccaBuco = () => {
    if (!ultimo) return avanti();
    const el = document.querySelector<HTMLElement>(passo.sel);
    fine();
    el?.click();
  };
  const alto = typeof window !== "undefined" ? window.innerHeight : 800;
  // il fumetto sotto la cosa se c'è posto, altrimenti sopra
  const sotto = !box || box.y + box.h + 210 < alto || box.y < 230;
  const stileFumetto: CSSProperties = box ? (sotto ? { top: Math.min(box.y + box.h + 14, alto - 200) } : { bottom: Math.max(alto - box.y + 14, 12) }) : { top: "40%" };

  return createPortal(
    <div className="ld-guida" role="dialog" aria-modal="true" aria-label={t("guida.etichetta")}>
      {box && (
        <button
          type="button"
          className="ld-guida-buco"
          style={{ left: box.x, top: box.y, width: box.w, height: box.h, borderRadius: box.r }}
          aria-label={ultimo ? t("guida.tocca_qui") : t("guida.avanti")}
          onClick={toccaBuco}
        />
      )}
      {!box && <div className="ld-guida-scuro" />}
      <div className="ld-guida-fumetto" data-dove={sotto ? "sotto" : "sopra"} style={stileFumetto} key={n}>
        <Mascotte dimensione={64} classe="ld-guida-mascotte" />
        <div className="ld-guida-nuvola">
          <p aria-live="polite">{passo.testo}</p>
          <div className="ld-guida-azioni">
            <span className="ld-guida-punti" aria-label={t("guida.passo", { n: n + 1, di: validi.length })}>
              {validi.map((_, i) => (
                <span key={i} data-attivo={i === n || undefined} />
              ))}
            </span>
            {!ultimo && (
              <button type="button" className="pulsante-link" onClick={fine}>
                {t("guida.salta")}
              </button>
            )}
            <button type="button" className="pulsante pulsante-primario" ref={bottoneRif} onClick={avanti}>
              {ultimo ? t("guida.ho_capito") : t("guida.avanti")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
